/**
 * Walk-in counter sale dialog (DNKADM-0308; DNKAPI-0497 / DNKADM-0347 widen it to the whole
 * catalog) — provision + auto-invite a brand-new customer and sell them ONE cart in one call,
 * `POST /v2/admin/credit-purchases/walk-in` (`@/api/walkInSale`). Answers the one Front Desk task
 * with no UI at all: selling to someone standing at the counter who isn't in the system yet,
 * without the older two-step (invite in Users, wait, then sell from the member's own Sell dialog).
 *
 * **DNKAPI-0497 rewired the endpoint onto the SAME order surface `SellCartDialog`/
 * `BuyMembershipOrderDialog` use** (route unchanged), so a walk-in can now be sold a package or a
 * membership plan, not only credits. What is genuinely still true after 0497 (see `@/api/walkInSale`'s
 * header for the full contract):
 *  - **No pricing/quote endpoint** — a walk-in has no `contactId` until the sale is submitted, so
 *    there is nothing to quote against. The running total shown while building the cart is
 *    computed CLIENT-SIDE (from list prices / negotiated edits), labelled "before tax" throughout.
 *    The CONFIRMATION shows the server's real receipt (`subtotal`/`taxTotal`/`grandTotal`) — never
 *    the client estimate.
 *  - **Card mode never offers a card on file** — a walk-in has, by definition, no member record
 *    yet, so there is nothing to choose from; every card charge tokenizes fresh.
 *
 * **One kind of item per sale (DNKADM-0347).** The order surface returns a stable 400
 * `UNSUPPORTED_IN_SLICE` for a `membership_plan` line mixed with any one-time line, until Slice 3
 * ships combined checkout. Rather than build a cart that permits the combination and then explains
 * a rejection, this dialog makes it UNREPRESENTABLE: after contact entry, the front desk picks
 * exactly one of Membership / Package / Credits, and the cart step that follows offers only that
 * kind. Changing the choice clears the cart (confirmed first if it isn't empty). Membership mode
 * holds exactly one plan — picking a second replaces the first, since a subscription is one plan.
 * `external` payment is unavailable in Membership mode (a subscription's renewals cannot bill
 * against cash in a drawer) — the mode choice makes this easy since the payment step already
 * knows which cart kind it is pricing.
 *
 * **Negotiated pricing (DNKADM-0334/0335) rides through, reusing the SAME shared machinery**
 * `SellCartDialog`/`BuyMembershipOrderDialog` use (`./negotiatedPriceOverride`,
 * `NegotiatedPricePinPrompt`) — same control, same PIN prompt, no second treatment. It applies in
 * Membership and Package modes; the server rejects it on a `credit` line, so Credits mode never
 * offers it.
 *
 * **Payment is card / external only — no Comp mode.** The order surface's `Payment.mode` accepts
 * `comp` too (and this unit's API layer, `@/api/walkInSale`, reuses that shape verbatim), but this
 * spec's Units never ask for a Comp control in THIS dialog — only `reason`/`paymentMethodKind`/
 * `cardId` are called out as gained fields, and comp only appears in the spec's Background section
 * as context. Adding a Comp toggle here (with its manager-PIN gate) would be UI scope the spec
 * doesn't cover, so it is deliberately left out of this unit (see the delivery's Notes for
 * Architect for the follow-up opportunity).
 *
 * **Deliberately NOT `SellCartDialog`.** This dialog is still its own, simpler build rather than a
 * merge of the two — a full convergence (they now talk to the same shapes) is worth doing but is
 * its own unit with its own risk (see the DNKADM-0347 spec's Non-Goals). Catalog-fetching (the same
 * `listCreditBuckets`/`listPackages`/`listMembershipPlans` API calls `SellCartDialog`/
 * `BuyMembershipOrderDialog` use) and the shared pricing/PIN machinery ARE reused directly; the
 * small row-normalizer helpers below (`toBucketRow`, `toPackageRow`, `sanitizeCap`, …) are
 * intentionally DUPLICATED rather than imported — they are `SellCartDialog`-local (unexported), and
 * this file already established the precedent of duplicating them rather than modifying
 * `SellCartDialog` to export them (see the DNKADM-0347 delivery's Notes for Architect).
 *
 * **DNKADM-0354 — recognize an email that already belongs to someone.** `upsertContactByEmail`
 * (create-or-find) means a typo'd or reused email lands on the RIGHT contact, but nothing on
 * screen used to say so — a typo could still mint a real duplicate contact, and staff had no
 * signal steering them back to the member's own Sell dialog (saved card, real history) instead of
 * this one. This dialog now matches the typed email, client-side and normalized (trim + lowercase
 * — the same identity key the server uses), against the SAME `listUsers` roster the Book-on-behalf
 * picker (`DNKADM-0349`) already fetches once per dialog open — **no API change, no second
 * fetch**. A match shows who it is (name + account state) and a link to their profile; it is a
 * convenience only (the roster is a snapshot from when the dialog opened) and never blocks or
 * gates the sale, which still works correctly either way. **DNKAPI-0508** closed the matching
 * stop condition this unit surfaced (a matched contact's stored name is now kept server-side, the
 * typed name used for nothing) and added `contactMatched`/`matchedFirstName`/`matchedLastName` to
 * the response; the confirmation consumes that signal to name the person using the server-KEPT
 * identity ("Peyton1 Dowdle (pbdowdle+1@gmail.com) already has access...") rather than echoing
 * whatever staff typed, so a wrong-person mistake is still catchable after the fact.
 *
 * **DNKADM-0472 — a parked card is driven, never left.** A card sale that comes back `PENDING`
 * with a `paymentAction` (3-D Secure or a redirect) is driven the way `SellCartDialog` drives
 * one, then the order is re-read through the member order read (`contactId` + `orderId`). A
 * `COMPLETED` read shows the receipt. A read still `PENDING` keeps the form locked on that order
 * ("Order <number>: Waiting on the card. Check Payments before charging again."), and its "Check
 * again" reads the order rather than re-sending the sale, so the card is never charged a second
 * time from this dialog while it waits.
 */
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { IconAction } from '@/components/IconAction';
import { listCreditBuckets, type CreditBucket } from '@/api/creditBuckets';
import { listPackages, type Offering } from '@/api/offerings';
import { listMembershipPlans, type MembershipPlan } from '@/api/membershipPlans';
import { usePaymentMethodPickList, type PaymentMethodOption } from '@/api/paymentMethods';
import { newIdempotencyKey } from '@/api/payments';
import { serverMessage } from '@/api/serverMessage';
import { v2ErrorCode } from '@/api/v2Errors';
import { listUsers, type UserRow } from '@/api/users';
import {
  getMemberOrder,
  type MembershipOrder,
  type OpenOrderNeedsAttention,
} from '@/api/membershipOrders';
import {
  createWalkInSale,
  toWalkInSaleErrorView,
  type WalkInLineInput,
  type WalkInPaymentInput,
  type WalkInSaleResult,
} from '@/api/walkInSale';
import CardPaymentField, { type TokenizeFn } from '@/components/payments/CardPaymentField';
import { PickListLoader } from '@/components/PickListLoader';
import { useCurrentOperator } from '@/auth/authHooks';
import { useResource } from '@/hooks/useResource';
import { useServiceLocation } from '@/location/useServiceLocation';
import { personLabel } from '@/utils/personLabel';
import { formatCreditAmount, formatMoney } from '@/utils/v2Format';
import { isNeedsReview, unsettledOrderMessage } from '@/domain/orderStatus';
import { scrubBrand } from '@/utils/whitelabel';
import { Icon, inkAlpha, MONO, resolveTokens } from '@/theme';
import { accountStateBadge } from './accountState';
import { NegotiatedPricePinPrompt } from './NegotiatedPricePinPrompt';
import {
  majorPriceInput,
  negotiatedPriceErrorView,
  negotiatedPriceToMinor,
  useNegotiatedPriceOverride,
} from './negotiatedPriceOverride';
import { getPaymentActionDriver, UnsupportedPaymentActionError } from './paymentActionDriver';
import {
  CHECK_AGAIN_FAILED,
  outcomeIsUnknown,
  unconfirmedClosePrompt,
  unconfirmedMessage,
} from './unconfirmedOutcome';
import { OrderNeedsAttentionAlert } from './OrderNeedsAttentionAlert';
import { withOpenOrder } from './orderNeedsAttention';

/**
 * No currency travels this surface at all (no quote, no tenant-currency field on a bucket/
 * offering/plan) — this is a best-effort label for the client-computed estimate only, mirroring
 * `SellCartDialog`'s own fallback for the same reason. The CONFIRMATION uses the server's own
 * `currency` instead.
 */
const FALLBACK_CURRENCY = 'USD';

type SaleMode = 'membership' | 'package' | 'credit';
type PaymentMode = 'card' | 'external';
type ExternalPaymentInput = Extract<WalkInPaymentInput, { mode: 'external' }>;
type ExternalStatus = ExternalPaymentInput['status'];

export interface WalkInSaleDialogProps {
  open: boolean;
  onClose: () => void;
  /** Called once the sale call has resolved to a real outcome — a good time to refresh a roster. */
  onSold: () => void;
}

type CatalogState =
  | { status: 'idle' | 'loading' }
  | { status: 'ready'; buckets: CreditBucket[]; packages: Offering[]; plans: MembershipPlan[] }
  | { status: 'error'; message: string };

interface SubmitError {
  message: string;
  retryable: boolean;
  /**
   * DNKADM-0475 (F-99): set on a `409 ORDER_NEEDS_ATTENTION` — an earlier card order of this
   * member is still open, so the alert names it and links to Payments instead of a charge.
   */
  openOrder?: OpenOrderNeedsAttention;
}

/**
 * DNKADM-0467: a sale the server never confirmed either way. Its idempotency key is kept
 * and every field stays locked until "Check again" settles it under that key.
 */
interface Unconfirmed {
  message: string;
  /**
   * The card nonce the attempt sent, held in memory only so "Check again" re-sends the
   * SAME request. The server answers a key it has seen without touching the card.
   */
  cardToken: string | null;
  /**
   * DNKADM-0472: the order a parked card sale produced, once known. "Check again" then READS
   * it (the member order read) instead of re-sending the sale, so a parked card is never
   * charged a second time from this dialog.
   */
  order: ParkedOrderRef | null;
  /** Set when "Check again" could not read the order; the attempt stays locked. */
  readFailed?: boolean;
  /**
   * DNKADM-0475 (F-100): locked on a `NEEDS_REVIEW` order — the card was charged and nothing
   * applied — so the close prompt says so instead of "couldn't confirm".
   */
  needsReview?: boolean;
}

/** The contact + order a parked walk-in sale landed on — enough to re-read the order. */
interface ParkedOrderRef {
  contactId: string;
  orderId: number;
  /** The sale as first answered: its walk-in fields (invite outcome, kept name) outlive a re-read. */
  sale: WalkInSaleResult;
}

/**
 * Trim + lowercase — the SAME normalization the server uses as the identity key
 * (`AdminWalkInSaleRequest.WalkInContact.email`), so the client-side roster match (DNKADM-0354
 * U1) agrees with what the server will actually match on. `null` for empty/whitespace-only.
 */
function normalizeEmail(value: string | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  const trimmed = value.trim().toLowerCase();
  return trimmed.length > 0 ? trimmed : null;
}

/** A sellable credit bucket, normalized for the cart row + client-side estimate. */
interface BucketRow {
  key: string;
  refId: number;
  name: string;
  pricePerCredit: number;
  cap: number | null;
}

/** A sellable package, normalized for the cart row + client-side estimate. */
interface PackageRow {
  key: string;
  refId: number;
  name: string;
  price: number;
  grants: string[];
}

export function WalkInSaleDialog({ open, onClose, onSold }: WalkInSaleDialogProps) {
  if (!open) return null;
  return <WalkInSaleDialogBody onClose={onClose} onSold={onSold} />;
}

function WalkInSaleDialogBody({
  onClose,
  onSold,
}: {
  onClose: () => void;
  onSold: () => void;
}) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  const ids = useId();
  const titleId = `${ids}-title`;
  const navigate = useNavigate();
  const location = useServiceLocation();
  const operator = useCurrentOperator();
  const tenantSlug = operator?.tenantSlug?.trim() ?? '';

  const [catalog, setCatalog] = useState<CatalogState>({ status: 'idle' });
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [saleMode, setSaleMode] = useState<SaleMode | null>(null);
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [selectedPackageIds, setSelectedPackageIds] = useState<string[]>([]);
  /** Presence of a key means the package price was edited; absence means list price. */
  const [packagePriceEdits, setPackagePriceEdits] = useState<Record<string, string>>({});
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  /** Presence means the operator edited the list rate; absence preserves list price. */
  const [membershipRateEdit, setMembershipRateEdit] = useState<string | undefined>(undefined);
  const [paymentMode, setPaymentMode] = useState<PaymentMode>('card');
  const [externalStatus, setExternalStatus] = useState<ExternalStatus>('paid');
  const [paymentMethodId, setPaymentMethodId] = useState('');
  const [cardReady, setCardReady] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<SubmitError | null>(null);
  const [result, setResult] = useState<WalkInSaleResult | null>(null);
  const [unconfirmed, setUnconfirmed] = useState<Unconfirmed | null>(null);
  const attemptKeyRef = useRef<string | null>(null);
  const tokenizeRef = useRef<TokenizeFn | null>(null);

  const methods = usePaymentMethodPickList();

  // DNKADM-0354 — the whole users roster, fetched ONCE when the dialog opens (this component is
  // freshly mounted per open, same as `loadCatalog` above), reusing the SAME `listUsers` call the
  // Book-on-behalf picker (`DNKADM-0349`) already fetches — no API change, no second endpoint.
  // `GET /v2/admin/users` has no `search`/paging param: correct to fetch-all-and-match
  // client-side at 24 users (Sim Creek today), wrong at 2,000 — the fix then is a `search` param
  // on `/v2/admin/users`, not a fetch-per-keystroke bolted onto this dialog (mirrors the 0349 stop
  // condition; do not build that here).
  const usersRoster = useResource(() => listUsers(), []);

  const locationId = location.selectedId !== null ? Number(location.selectedId) : Number.NaN;
  const locationReady = location.status === 'ready' && Number.isFinite(locationId);
  const tenantReady = tenantSlug.length > 0;

  // The typed email matched against the once-fetched roster, normalized (trim + lowercase — the
  // same identity key `AdminWalkInSaleRequest.WalkInContact.email` uses server-side) on both
  // sides. `null` whenever the typed email is empty/whitespace-only or matches nobody; recomputed
  // on every render off `email`, so editing or clearing it clears the match for free.
  const normalizedEmail = normalizeEmail(email);
  // This roster (`GET /v2/admin/users`) is customers only by construction (DNKADM-0367),
  // so an email matches at most one row.
  const matchedUser: UserRow | null =
    normalizedEmail === null
      ? null
      : ((usersRoster.data ?? []).find((u) => normalizeEmail(u.email) === normalizedEmail) ?? null);
  const matchedContactId = matchedUser?.contactId ?? null;

  const loadCatalog = useCallback(() => {
    setCatalog({ status: 'loading' });
    // All three catalogs in parallel; any one failing is one error + Retry, never a
    // half-catalog that silently hides a sellable row.
    void Promise.all([
      listCreditBuckets({ status: 'active' }),
      listPackages('active'),
      listMembershipPlans({ status: 'active' }),
    ])
      .then(([buckets, packages, plans]) => setCatalog({ status: 'ready', buckets, packages, plans }))
      .catch((error: unknown) =>
        setCatalog({
          status: 'error',
          message: serverMessage(error) ?? 'The sellable catalog could not be loaded.',
        }),
      );
  }, []);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  const bucketRows: BucketRow[] =
    catalog.status === 'ready' ? catalog.buckets.flatMap(toBucketRow) : [];
  const packageRows: PackageRow[] =
    catalog.status === 'ready'
      ? catalog.packages.flatMap((offering) => toPackageRow(offering, location.selectedId))
      : [];
  const plans: MembershipPlan[] = catalog.status === 'ready' ? catalog.plans : [];
  const selectedPlan = plans.find((plan) => plan.id === selectedPlanId) ?? null;

  const lines: WalkInLineInput[] = [];
  if (saleMode === 'credit') {
    for (const row of bucketRows) {
      const qty = quantityFor(quantities, row);
      if (qty >= 1) lines.push({ type: 'credit', refId: row.refId, qty });
    }
  } else if (saleMode === 'package') {
    for (const row of packageRows) {
      if (!selectedPackageIds.includes(row.key)) continue;
      const edit = packagePriceEdits[row.key];
      const overrideBase = edit === undefined ? null : negotiatedPriceToMinor(edit, FALLBACK_CURRENCY);
      lines.push(
        overrideBase === null
          ? { type: 'package', refId: row.refId }
          : { type: 'package', refId: row.refId, overrideBase },
      );
    }
  } else if (saleMode === 'membership' && selectedPlan !== null) {
    const refId = Number(selectedPlan.id);
    const overrideBase =
      membershipRateEdit === undefined ? null : negotiatedPriceToMinor(membershipRateEdit, FALLBACK_CURRENCY);
    if (Number.isFinite(refId)) {
      lines.push(
        overrideBase === null
          ? { type: 'membership_plan', refId }
          : { type: 'membership_plan', refId, overrideBase },
      );
    }
  }

  const estimatedSubtotal = estimateSubtotal(
    saleMode,
    bucketRows,
    packageRows,
    quantities,
    selectedPackageIds,
    packagePriceEdits,
    selectedPlan,
    membershipRateEdit,
  );

  const membershipRateInvalid =
    saleMode === 'membership' &&
    membershipRateEdit !== undefined &&
    negotiatedPriceToMinor(membershipRateEdit, FALLBACK_CURRENCY) === null;
  const hasInvalidPackagePrice =
    saleMode === 'package' &&
    packageRows.some((row) => {
      if (!selectedPackageIds.includes(row.key)) return false;
      const edit = packagePriceEdits[row.key];
      return edit !== undefined && negotiatedPriceToMinor(edit, FALLBACK_CURRENCY) === null;
    });
  const hasNegotiatedPrice = lines.some((line) => lineOverrideBase(line) !== undefined);
  const negotiatedPrice = useNegotiatedPriceOverride(hasNegotiatedPrice);
  const overrideAuth = negotiatedPrice.overrideAuth;

  const sold = result !== null;
  // In flight, or a sale whose outcome is unconfirmed (DNKADM-0467): no edit may start a
  // new attempt while the last one may still land.
  const locked = submitting || unconfirmed !== null;

  /** Any field/cart/mode edit starts a NEW attempt — a fresh idempotency key, no stale error. */
  function clearAttemptForEdit() {
    attemptKeyRef.current = null;
    setSubmitError(null);
  }

  function setQuantity(row: BucketRow, next: number) {
    clearAttemptForEdit();
    const bounded = boundQuantity(next, row.cap);
    setQuantities((prev) => ({ ...prev, [row.key]: bounded }));
  }

  function togglePackage(row: PackageRow, selected: boolean) {
    clearAttemptForEdit();
    negotiatedPrice.invalidate();
    if (!selected) {
      setPackagePriceEdits((prev) => {
        const next = { ...prev };
        delete next[row.key];
        return next;
      });
    }
    setSelectedPackageIds((prev) =>
      selected ? (prev.includes(row.key) ? prev : [...prev, row.key]) : prev.filter((id) => id !== row.key),
    );
  }

  function setPackagePrice(row: PackageRow, value: string) {
    clearAttemptForEdit();
    negotiatedPrice.invalidate();
    setPackagePriceEdits((prev) => ({ ...prev, [row.key]: value }));
  }

  /** Picking a plan REPLACES any prior selection — Membership mode holds exactly one line. */
  function selectPlan(id: string) {
    clearAttemptForEdit();
    negotiatedPrice.invalidate();
    setMembershipRateEdit(undefined);
    setSelectedPlanId(id.length > 0 ? id : null);
  }

  function applyNegotiatedPrice() {
    clearAttemptForEdit();
    negotiatedPrice.apply();
  }

  function isCartNonEmpty(): boolean {
    if (saleMode === 'credit') return bucketRows.some((row) => quantityFor(quantities, row) > 0);
    if (saleMode === 'package') return selectedPackageIds.length > 0;
    if (saleMode === 'membership') return selectedPlanId !== null;
    return false;
  }

  function clearCart() {
    setQuantities({});
    setSelectedPackageIds([]);
    setPackagePriceEdits({});
    setSelectedPlanId(null);
    setMembershipRateEdit(undefined);
    negotiatedPrice.reset();
  }

  /** Changing what's being sold clears the cart (confirmed first if it isn't empty). */
  function chooseSaleMode(next: SaleMode) {
    if (next === saleMode || locked) return;
    if (isCartNonEmpty() && !window.confirm('Switching what you’re selling clears the current cart. Continue?')) {
      return;
    }
    clearAttemptForEdit();
    clearCart();
    // External is unavailable for a membership — drop back to card if it was selected.
    if (next === 'membership' && paymentMode === 'external') setPaymentMode('card');
    setSaleMode(next);
  }

  const contactOk =
    firstName.trim().length > 0 && lastName.trim().length > 0 && email.trim().length > 0;
  const cartOk = lines.length > 0;

  const cardReadyToSubmit = tenantReady && cardReady && tokenizeRef.current !== null;
  const methodIdUsable = paymentMethodId.trim().length > 0;
  const externalReadyToSubmit = externalStatus === 'pending' || methodIdUsable;
  const modeReadyToSubmit = paymentMode === 'card' ? cardReadyToSubmit : externalReadyToSubmit;
  const canSubmit =
    contactOk &&
    saleMode !== null &&
    cartOk &&
    locationReady &&
    !hasInvalidPackagePrice &&
    !membershipRateInvalid &&
    (!hasNegotiatedPrice || overrideAuth !== undefined) &&
    modeReadyToSubmit &&
    unconfirmed === null &&
    !submitting;

  /**
   * DNKADM-0467: an unconfirmed sale may still land, and a sale started after leaving would
   * carry a new key — so leaving is confirmed, never silently allowed.
   */
  function confirmLeave(): boolean {
    return unconfirmed === null || window.confirm(unconfirmedClosePrompt(unconfirmed.needsReview));
  }

  function close() {
    if (submitting) return;
    if (!confirmLeave()) return;
    onClose();
  }

  function goToMember() {
    const contactId = result?.contactId;
    if (contactId === null || contactId === undefined) return;
    onClose();
    navigate(`/members/${encodeURIComponent(contactId)}`);
  }

  /** DNKADM-0354 U1's "Open their profile to sell" link — the better path than this dialog. */
  function viewMatchedProfile(contactId: string) {
    if (!confirmLeave()) return;
    onClose();
    navigate(`/members/${encodeURIComponent(contactId)}`);
  }

  async function submit() {
    setSubmitError(null);
    if (!contactOk) {
      setSubmitError({ retryable: false, message: 'Name and email are required.' });
      return;
    }
    if (saleMode === null) {
      setSubmitError({ retryable: false, message: 'Choose what you’re selling.' });
      return;
    }
    if (!cartOk) {
      setSubmitError({ retryable: false, message: cartEmptyMessage(saleMode) });
      return;
    }
    if (!locationReady) {
      setSubmitError({ retryable: false, message: locationMessage(location.status) });
      return;
    }
    if (hasNegotiatedPrice && overrideAuth === undefined) {
      setSubmitError({
        retryable: false,
        message: 'Apply the negotiated price before placing this sale.',
      });
      return;
    }
    if (paymentMode === 'external' && !externalReadyToSubmit) {
      setSubmitError({
        retryable: false,
        message: 'Choose the payment method this sale was paid with.',
      });
      return;
    }
    if (paymentMode === 'card' && (!cardReady || tokenizeRef.current === null)) {
      setSubmitError({ retryable: false, message: 'Finish entering the card before charging.' });
      return;
    }

    setSubmitting(true);
    let cardToken: string | null = null;
    if (paymentMode === 'card') {
      // Tokenizing happens BEFORE the request: a failure here sent nothing, so it is a
      // plain error and never an unconfirmed sale.
      try {
        cardToken = await requireTokenize()();
      } catch (error) {
        setSubmitError(toWalkInErrorView(error));
        setSubmitting(false);
        return;
      }
    }
    await placeSale(cardToken);
  }

  /**
   * Send the attempt — card (`cardToken` set) or external (`cardToken` null). One key per
   * ATTEMPT, reused on a retry and on "Check again": the server answers a key it has seen
   * with the order that key already produced, so nothing is charged or recorded twice.
   */
  async function placeSale(cardToken: string | null) {
    setSubmitting(true);
    try {
      const idempotencyKey = attemptKeyRef.current ?? newIdempotencyKey();
      attemptKeyRef.current = idempotencyKey;
      const contact = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        ...(phone.trim().length > 0 ? { phone: phone.trim() } : {}),
      };
      const payment: WalkInPaymentInput =
        cardToken !== null
          ? { mode: 'card', cardToken, locationId, idempotencyKey }
          : externalPayment(locationId, idempotencyKey, externalStatus, paymentMethodId);
      const sale = await createWalkInSale({
        contact,
        lines,
        payment,
        ...(overrideAuth === undefined ? {} : { overrideAuth }),
      });
      // DNKADM-0472: a card parked for 3-D Secure or a redirect is DRIVEN and then RE-READ,
      // as the sell cart does; the read is authoritative.
      const parked = sale.status === 'PENDING' && sale.paymentAction !== null;
      const order = parked ? parkedOrderRef(sale) : null;
      if (parked && order !== null) {
        await settleParkedSale(order, cardToken);
        return;
      }
      finishSale(sale, cardToken, order);
    } catch (error) {
      // DNKADM-0475 (0467 follow-up b): `503 CONTACT_UNAVAILABLE` is thrown while the contact
      // is found or created, before any order or charge (DNKAPI-0690 contract), so on a FIRST
      // send it is a definitive refusal. After an unconfirmed send it proves nothing about
      // that send, so it keeps the lock like any other 5xx.
      const refusedBeforeCharge =
        unconfirmed === null && v2ErrorCode(error) === 'CONTACT_UNAVAILABLE';
      if (outcomeIsUnknown(error) && !refusedBeforeCharge) {
        // DNKADM-0475: a re-send of a NEEDS_REVIEW sale that gets no answer leaves what we
        // already know (charged, nothing applied) in place.
        if (unconfirmed?.needsReview === true) {
          setUnconfirmed({ ...unconfirmed, readFailed: true });
          return;
        }
        // DNKADM-0467: the server may have committed this sale. Keep the key and lock the
        // form; only "Check again", under this same key, can settle it.
        setUnconfirmed({
          message: unconfirmedMessage(cardToken === null ? 'external' : 'card'),
          cardToken,
          order: null,
        });
        return;
      }
      // A definitive refusal: nothing was written, so the next submit is a new attempt.
      // DNKADM-0458: the negotiated-price PIN ends with the attempt too.
      attemptKeyRef.current = null;
      setUnconfirmed(null);
      if (overrideAuth !== undefined) negotiatedPrice.reset();
      setSubmitError(withOpenOrder(error, toWalkInErrorView(error)));
    } finally {
      setSubmitting(false);
    }
  }

  /**
   * Drive the parked card's `paymentAction`, then re-read the order (DNKADM-0472). A driver
   * that refuses falls through to the read, exactly as in the sell cart. A read that fails
   * keeps the sale locked on its order, so "Check again" reads it rather than charging again.
   */
  async function settleParkedSale(order: ParkedOrderRef, cardToken: string | null) {
    const { sale } = order;
    try {
      await drivePaymentAction(sale.paymentAction);
    } catch (error) {
      if (!(error instanceof UnsupportedPaymentActionError)) throw error;
    }
    let read: MembershipOrder;
    try {
      read = await getMemberOrder(order.contactId, order.orderId);
    } catch {
      setUnconfirmed({ message: parkedSaleMessage(sale), cardToken, order, readFailed: true });
      return;
    }
    finishSale(withOrderRead(sale, read), cardToken, order);
  }

  /**
   * The three outcomes of a sale or of its re-read. `COMPLETED` shows the receipt. `PENDING`
   * is not terminal (DNKADM-0467): the attempt keeps its key and stays locked, and a parked
   * card names its order and says to check Payments before charging again (DNKADM-0472).
   * `NEEDS_REVIEW` (charged, nothing applied) is held the same way. Anything else is
   * terminal and ends the attempt.
   */
  function finishSale(sale: WalkInSaleResult, cardToken: string | null, order: ParkedOrderRef | null) {
    if (sale.status === 'COMPLETED') {
      attemptKeyRef.current = null;
      setUnconfirmed(null);
      setResult(sale);
      onSold();
      return;
    }
    if (sale.status === 'PENDING') {
      setUnconfirmed({
        message:
          order === null && sale.paymentAction === null
            ? terminalSaleMessage(sale)
            : parkedSaleMessage(sale),
        cardToken,
        order,
      });
      return;
    }
    if (isNeedsReview(sale.status)) {
      // The card was charged and nothing applied: held like PENDING, never a fresh key, and
      // "Check again" reads the order when it is known.
      setUnconfirmed({ message: terminalSaleMessage(sale), cardToken, order, needsReview: true });
      return;
    }
    attemptKeyRef.current = null;
    setUnconfirmed(null);
    setSubmitError({ retryable: false, message: terminalSaleMessage(sale) });
  }

  /**
   * DNKADM-0467: settle an unconfirmed sale under its OWN key before anything new is allowed.
   * A parked card's order is READ (DNKADM-0472); until an order is known the same request is
   * re-sent, which the server answers without charging again.
   */
  async function checkAgain() {
    if (unconfirmed === null || submitting) return;
    setSubmitError(null);
    const { order, cardToken } = unconfirmed;
    if (order === null) {
      await placeSale(cardToken);
      return;
    }
    setSubmitting(true);
    try {
      const read = await getMemberOrder(order.contactId, order.orderId);
      finishSale(withOrderRead(order.sale, read), cardToken, order);
    } catch {
      setUnconfirmed((current) => (current === null ? null : { ...current, readFailed: true }));
    } finally {
      setSubmitting(false);
    }
  }

  function requireTokenize(): TokenizeFn {
    const tokenize = tokenizeRef.current;
    if (tokenize === null) throw new Error('Card entry is not ready.');
    return tokenize;
  }

  return (
    <Dialog open onClose={close} maxWidth="sm" fullWidth aria-labelledby={titleId}>
      <DialogTitle
        id={`${ids}-header`}
        sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, pb: 0.5 }}
      >
        <Avatar
          variant="rounded"
          sx={{ bgcolor: t.brandSoft, color: t.brand, width: 38, height: 38 }}
        >
          <Icon name="point_of_sale" sx={{ fontSize: 21 }} />
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography id={titleId} sx={{ fontSize: 18, fontWeight: 500 }}>
            Sell to a walk-in
          </Typography>
          <Typography variant="body2">New customer — membership, package or credits</Typography>
        </Box>
        <IconAction size="small" onClick={close} label="Close" disabled={submitting}>
          <Icon name="close" sx={{ fontSize: 20 }} />
        </IconAction>
      </DialogTitle>

      <DialogContent>
        {sold ? (
          <WalkInSaleConfirmation
            result={result}
            firstName={firstName}
            lastName={lastName}
            email={email}
          />
        ) : (
          <Stack spacing={2}>
            {!locationReady && (
              <Alert
                severity="warning"
                action={
                  location.status === 'error' ? (
                    <Button color="inherit" size="small" onClick={location.retry}>
                      Retry
                    </Button>
                  ) : undefined
                }
              >
                {locationMessage(location.status)}
              </Alert>
            )}

            <SectionHeading icon="badge" text="Customer" />
            <Stack direction="row" spacing={1.5}>
              <TextField
                label="First name"
                required
                fullWidth
                size="small"
                value={firstName}
                onChange={(e) => {
                  clearAttemptForEdit();
                  setFirstName(e.target.value);
                }}
                disabled={locked}
              />
              <TextField
                label="Last name"
                required
                fullWidth
                size="small"
                value={lastName}
                onChange={(e) => {
                  clearAttemptForEdit();
                  setLastName(e.target.value);
                }}
                disabled={locked}
              />
            </Stack>
            <TextField
              label="Email"
              type="email"
              required
              fullWidth
              size="small"
              value={email}
              onChange={(e) => {
                clearAttemptForEdit();
                setEmail(e.target.value);
              }}
              disabled={locked}
              helperText="We'll invite them at this address so they can claim the credits."
            />

            {matchedUser !== null && (
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>{email.trim()}</strong> already belongs to{' '}
                  {personLabel(matchedUser.name, matchedUser.email)} (
                  {accountStateBadge(matchedUser, theme.palette.mode).label}). The name above
                  won&apos;t be saved — their existing name is kept.
                </Typography>
                {matchedContactId !== null && (
                  <Button
                    size="small"
                    sx={{ mt: 0.75 }}
                    onClick={() => viewMatchedProfile(matchedContactId)}
                  >
                    Open their profile to sell
                  </Button>
                )}
              </Alert>
            )}

            <TextField
              label="Phone (optional)"
              fullWidth
              size="small"
              value={phone}
              onChange={(e) => {
                clearAttemptForEdit();
                setPhone(e.target.value);
              }}
              disabled={locked}
            />

            <SectionHeading icon="storefront" text="What are you selling?" />
            <ToggleButtonGroup
              exclusive
              size="small"
              value={saleMode}
              onChange={(_event, value: SaleMode | null) => {
                if (value === null) return;
                chooseSaleMode(value);
              }}
              aria-label="What are you selling"
              sx={{ alignSelf: 'flex-start' }}
            >
              <ToggleButton value="membership" aria-label="Membership">
                Membership
              </ToggleButton>
              <ToggleButton value="package" aria-label="Package">
                Package
              </ToggleButton>
              <ToggleButton value="credit" aria-label="Credits">
                Credits
              </ToggleButton>
            </ToggleButtonGroup>

            {saleMode === null && (
              <Alert severity="info">Choose what you&apos;re selling to build the cart.</Alert>
            )}

            {catalog.status === 'loading' && saleMode !== null && (
              <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                <CircularProgress size={18} />
                <Typography variant="body2">Loading what can be sold...</Typography>
              </Stack>
            )}
            {catalog.status === 'error' && saleMode !== null && (
              <Alert
                severity="error"
                role="alert"
                action={
                  <Button color="inherit" size="small" onClick={loadCatalog}>
                    Retry
                  </Button>
                }
              >
                {catalog.message}
              </Alert>
            )}

            {saleMode === 'credit' && (
              <>
                {catalog.status === 'ready' && bucketRows.length === 0 && (
                  <Alert severity="info">No sellable credit buckets are priced for this tenant yet.</Alert>
                )}
                {bucketRows.map((row) => (
                  <BucketCartRow
                    key={row.key}
                    row={row}
                    qty={quantityFor(quantities, row)}
                    disabled={locked}
                    onChange={(next) => setQuantity(row, next)}
                  />
                ))}
              </>
            )}

            {saleMode === 'package' && (
              <>
                {catalog.status === 'ready' && packageRows.length === 0 && (
                  <Alert severity="info">No sellable packages at this location.</Alert>
                )}
                {packageRows.map((row) => (
                  <PackageCartRow
                    key={row.key}
                    row={row}
                    selected={selectedPackageIds.includes(row.key)}
                    priceEdit={packagePriceEdits[row.key]}
                    priceInvalid={
                      packagePriceEdits[row.key] !== undefined &&
                      negotiatedPriceToMinor(packagePriceEdits[row.key] ?? '', FALLBACK_CURRENCY) === null
                    }
                    allowPriceOverride
                    disabled={locked}
                    onToggle={(selected) => togglePackage(row, selected)}
                    onPriceChange={(value) => setPackagePrice(row, value)}
                  />
                ))}
              </>
            )}

            {saleMode === 'membership' && (
              <>
                {catalog.status === 'ready' && plans.length === 0 ? (
                  <Alert severity="info">No membership plans available to sell.</Alert>
                ) : (
                  <TextField
                    label="Plan"
                    select
                    required
                    fullWidth
                    size="small"
                    value={plans.some((plan) => plan.id === selectedPlanId) ? (selectedPlanId ?? '') : ''}
                    onChange={(e) => selectPlan(e.target.value)}
                    disabled={locked || catalog.status !== 'ready'}
                    helperText={
                      selectedPlan
                        ? `${formatMoney(selectedPlan.price, FALLBACK_CURRENCY)} · ${selectedPlan.termDays} days`
                        : ' '
                    }
                  >
                    {catalog.status === 'loading' && (
                      <MenuItem value="" disabled>
                        Loading plans...
                      </MenuItem>
                    )}
                    {plans.map((plan) => (
                      <MenuItem key={plan.id} value={plan.id}>
                        {scrubBrand(plan.name) ?? plan.name} · {formatMoney(plan.price, FALLBACK_CURRENCY)}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
                {selectedPlan !== null && (
                  <TextField
                    label="Membership rate"
                    required
                    fullWidth
                    size="small"
                    value={membershipRateEdit ?? majorPriceInput(selectedPlan.price)}
                    onChange={(e) => {
                      clearAttemptForEdit();
                      negotiatedPrice.invalidate();
                      setMembershipRateEdit(e.target.value);
                    }}
                    disabled={locked}
                    error={membershipRateInvalid}
                    helperText={
                      membershipRateInvalid
                        ? 'Enter a valid rate greater than 0.'
                        : `Pre-tax rate in ${FALLBACK_CURRENCY}`
                    }
                    slotProps={{ htmlInput: { inputMode: 'decimal', autoComplete: 'off' } }}
                  />
                )}
              </>
            )}

            {saleMode !== null && lines.length > 0 && (
              <Box sx={{ border: `1px solid ${t.line}`, borderRadius: 1, bgcolor: t.surface, p: 1.5 }}>
                <Stack direction="row" sx={{ justifyContent: 'space-between', gap: 1 }}>
                  <Typography sx={{ fontSize: 13, color: inkAlpha(theme, 0.62) }}>
                    Full price (before tax)
                  </Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                    {formatMoney(estimatedSubtotal, FALLBACK_CURRENCY)}
                  </Typography>
                </Stack>
                <Typography sx={{ fontSize: 12, color: inkAlpha(theme, 0.62), mt: 0.5 }}>
                  An estimate only — the server computes the exact charge (tax included where it
                  applies) at checkout.
                </Typography>
              </Box>
            )}

            {hasNegotiatedPrice && (
              <NegotiatedPricePinPrompt
                draftPin={negotiatedPrice.draftPin}
                disabled={locked}
                onDraftChange={(value) => {
                  clearAttemptForEdit();
                  negotiatedPrice.setDraftPin(value);
                }}
                onApply={applyNegotiatedPrice}
              />
            )}

            <SectionHeading icon="payments" text="Payment" />
            <ToggleButtonGroup
              exclusive
              size="small"
              value={paymentMode}
              onChange={(_event, value: PaymentMode | null) => {
                if (value === null || value === paymentMode || locked) return;
                clearAttemptForEdit();
                setCardReady(false);
                tokenizeRef.current = null;
                setPaymentMode(value);
              }}
              aria-label="Payment mode"
              sx={{ alignSelf: 'flex-start' }}
            >
              <ToggleButton value="card" aria-label="Pay card">
                Pay card
              </ToggleButton>
              {saleMode !== 'membership' && (
                <ToggleButton value="external" aria-label="External">
                  External
                </ToggleButton>
              )}
            </ToggleButtonGroup>
            {saleMode === 'membership' && (
              <Typography variant="caption" sx={{ color: inkAlpha(theme, 0.62) }}>
                External isn&apos;t available for a membership — a subscription&apos;s renewals
                can&apos;t bill against cash in a drawer.
              </Typography>
            )}

            {paymentMode === 'card' &&
              (locationReady && tenantReady ? (
                <CardPaymentField
                  tenantSlug={tenantSlug}
                  locationRef={location.selectedId ?? ''}
                  disabled={locked}
                  onReady={(tokenize) => {
                    tokenizeRef.current = tokenize;
                    setCardReady(true);
                  }}
                  onError={() => {
                    tokenizeRef.current = null;
                    setCardReady(false);
                  }}
                />
              ) : (
                <Alert severity="warning">
                  {tenantReady
                    ? 'Select a location before charging the card.'
                    : 'Tenant payment context is unavailable. Sign in again before charging a card.'}
                </Alert>
              ))}

            {paymentMode === 'external' && (
              <>
                <TextField
                  label="Payment status"
                  select
                  required
                  fullWidth
                  size="small"
                  value={externalStatus}
                  onChange={(e) => {
                    clearAttemptForEdit();
                    setExternalStatus(e.target.value === 'pending' ? 'pending' : 'paid');
                  }}
                  disabled={locked}
                  helperText="External records how the sale was paid, never how much."
                >
                  <MenuItem value="paid">Paid</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                </TextField>

                {externalStatus === 'paid' && (
                  <Box>
                    <PickListLoader list={methods} label="payment methods" loadingVariant="inline">
                      {(methodOptions: PaymentMethodOption[]) => (
                        <TextField
                          label="Payment method"
                          select
                          required
                          fullWidth
                          size="small"
                          value={
                            methodOptions.some((m) => m.id === paymentMethodId) ? paymentMethodId : ''
                          }
                          onChange={(e) => {
                            clearAttemptForEdit();
                            setPaymentMethodId(e.target.value);
                          }}
                          disabled={locked}
                        >
                          {methodOptions.length === 0 && (
                            <MenuItem value="" disabled>
                              No payment methods available
                            </MenuItem>
                          )}
                          {methodOptions.map((m) => (
                            <MenuItem key={m.id} value={m.id}>
                              {scrubBrand(m.label) ?? m.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    </PickListLoader>
                  </Box>
                )}
              </>
            )}

            {unconfirmed !== null && (
              <Alert
                severity="warning"
                role="alert"
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => void checkAgain()}
                    disabled={submitting}
                  >
                    Check again
                  </Button>
                }
              >
                {unconfirmed.message}
                {unconfirmed.readFailed === true && (
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {CHECK_AGAIN_FAILED}
                  </Typography>
                )}
              </Alert>
            )}

            {submitError?.openOrder !== undefined && (
              <OrderNeedsAttentionAlert
                openOrder={submitError.openOrder}
                contactId={null}
                onLeave={onClose}
              />
            )}
            {submitError && submitError.openOrder === undefined && (
              <Alert
                severity="error"
                role="alert"
                action={
                  submitError.retryable ? (
                    <Button color="inherit" size="small" onClick={() => void submit()}>
                      Retry
                    </Button>
                  ) : undefined
                }
              >
                {submitError.message}
              </Alert>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ borderTop: `1px solid ${t.lineSoft}`, px: 2.5, py: 1.5 }}>
        <Button color="inherit" onClick={close} disabled={submitting} sx={{ color: t.text2 }}>
          {sold ? 'Done' : 'Cancel'}
        </Button>
        {sold && result?.contactId !== null && (
          <Button variant="outlined" startIcon={<Icon name="person" />} onClick={goToMember}>
            Go to this member
          </Button>
        )}
        {!sold && (
          <Button
            variant="contained"
            startIcon={<Icon name="check" />}
            onClick={() => void submit()}
            disabled={!canSubmit}
          >
            {submitting ? 'Charging...' : 'Sell to walk-in'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function SectionHeading({ icon, text }: { icon: string; text: string }) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  return (
    <Stack direction="row" sx={{ alignItems: 'center', gap: 0.75 }}>
      <Icon name={icon} sx={{ fontSize: 18, color: t.brand }} />
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: inkAlpha(theme, 0.7) }}>
        {text}
      </Typography>
    </Stack>
  );
}

function BucketCartRow({
  row,
  qty,
  disabled,
  onChange,
}: {
  row: BucketRow;
  qty: number;
  disabled: boolean;
  onChange: (next: number) => void;
}) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  const atCap = row.cap !== null && qty >= row.cap;
  return (
    <Box sx={{ border: `1px solid ${t.line}`, borderRadius: 1, p: 1.25, bgcolor: t.surface }}>
      <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 500 }}>{row.name}</Typography>
          <Typography variant="body2" sx={{ color: inkAlpha(theme, 0.62) }}>
            {`${formatMoney(row.pricePerCredit, FALLBACK_CURRENCY)} per credit`}
            {row.cap === null ? '' : ` · limit ${row.cap} per sale`}
          </Typography>
        </Box>
        <Stack
          direction="row"
          role="group"
          aria-label={`${row.name} quantity`}
          sx={{ alignItems: 'center', gap: 0.5 }}
        >
          <IconAction
            size="small"
            label={`Remove ${formatCreditAmount(1, row.name)}`}
            onClick={() => onChange(qty - 1)}
            disabled={disabled || qty <= 0}
          >
            <Icon name="remove" sx={{ fontSize: 18 }} />
          </IconAction>
          <Typography sx={{ minWidth: 24, textAlign: 'center', fontFamily: MONO, fontSize: 14 }}>
            {qty}
          </Typography>
          <IconAction
            size="small"
            label={`Add ${formatCreditAmount(1, row.name)}`}
            onClick={() => onChange(qty + 1)}
            disabled={disabled || atCap}
          >
            <Icon name="add" sx={{ fontSize: 18 }} />
          </IconAction>
        </Stack>
      </Stack>
    </Box>
  );
}

function PackageCartRow({
  row,
  selected,
  priceEdit,
  priceInvalid,
  allowPriceOverride,
  disabled,
  onToggle,
  onPriceChange,
}: {
  row: PackageRow;
  selected: boolean;
  priceEdit: string | undefined;
  priceInvalid: boolean;
  allowPriceOverride: boolean;
  disabled: boolean;
  onToggle: (selected: boolean) => void;
  onPriceChange: (value: string) => void;
}) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  return (
    <Box sx={{ border: `1px solid ${t.line}`, borderRadius: 1, p: 1.25, bgcolor: t.surface }}>
      <Stack direction="row" sx={{ alignItems: 'flex-start', gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={selected}
                disabled={disabled}
                onChange={(event) => onToggle(event.target.checked)}
              />
            }
            label={row.name}
            slotProps={{ typography: { sx: { fontSize: 14, fontWeight: 500 } } }}
          />
          <Typography variant="body2" sx={{ color: inkAlpha(theme, 0.62), pl: 3.75 }}>
            {formatMoney(row.price, FALLBACK_CURRENCY)}
            {row.grants.length === 0 ? '' : ` · ${row.grants.join(', ')}`}
          </Typography>
        </Box>
        {allowPriceOverride && (
          <TextField
            label={`Price for ${row.name}`}
            size="small"
            value={priceEdit ?? (selected ? majorPriceInput(row.price) : '')}
            onChange={(event) => onPriceChange(event.target.value)}
            disabled={disabled || !selected}
            error={priceInvalid}
            helperText={priceInvalid ? 'Enter a valid price greater than 0.' : ' '}
            slotProps={{
              input: {
                startAdornment: <InputAdornment position="start">{FALLBACK_CURRENCY}</InputAdornment>,
              },
              htmlInput: { inputMode: 'decimal', autoComplete: 'off' },
            }}
            sx={{ width: 190 }}
          />
        )}
      </Stack>
    </Box>
  );
}

/**
 * The confirmation (replaces the form entirely once `result` is set, mirroring
 * `SellCartDialog`'s DNKADM-0250 pattern). Only a settled sale (`COMPLETED`) reaches it: it
 * names the customer, states the invite outcome, and shows the SERVER's own receipt totals
 * (never the client "before tax" estimate — DNKADM-0347 U3). A parked card (DNKADM-0472) is
 * driven and re-read first; while it is still pending it stays on the locked form with its
 * order number, never here.
 */
function WalkInSaleConfirmation({
  result,
  firstName,
  lastName,
  email,
}: {
  result: WalkInSaleResult;
  firstName: string;
  lastName: string;
  email: string;
}) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  const name = `${firstName.trim()} ${lastName.trim()}`.trim() || email.trim();
  const currency = result.currency ?? FALLBACK_CURRENCY;

  return (
    <Stack role="status" spacing={2} sx={{ pt: 1 }}>
      <Stack spacing={0.5} sx={{ alignItems: 'center', textAlign: 'center' }}>
        <Avatar variant="rounded" sx={{ bgcolor: t.brandSoft, color: t.brand, width: 44, height: 44 }}>
          <Icon name="check_circle" sx={{ fontSize: 26 }} />
        </Avatar>
        <Typography sx={{ fontSize: 18, fontWeight: 600 }}>Sale complete</Typography>
        <Typography sx={{ fontFamily: MONO, fontSize: 28, fontWeight: 600 }}>
          {formatMoney(result.grandTotal, currency)}
        </Typography>
        <Typography variant="body2" sx={{ color: inkAlpha(theme, 0.62) }}>
          {contactSummaryLine(result, name, email)}
        </Typography>
      </Stack>
      <Box sx={{ border: `1px solid ${t.line}`, borderRadius: 1, p: 1.5, bgcolor: t.surface }}>
        <Stack spacing={1}>
          {result.orderNumber !== null && (
            <Stack direction="row" sx={{ justifyContent: 'space-between', gap: 1 }}>
              <Typography sx={{ fontSize: 13, color: inkAlpha(theme, 0.62) }}>Order</Typography>
              <Typography sx={{ fontFamily: MONO, fontSize: 13 }}>{result.orderNumber}</Typography>
            </Stack>
          )}
          <Divider />
          {result.lines.map((line, index) => (
            <MoneyRow
              key={`${line.type ?? 'line'}-${line.refId ?? index}`}
              label={scrubBrand(line.tier) ?? 'Name unavailable'}
              value={formatMoney(line.total, currency)}
            />
          ))}
          <Divider />
          <MoneyRow label="Subtotal" value={formatMoney(result.subtotal, currency)} />
          <MoneyRow label="Tax" value={formatMoney(result.taxTotal, currency)} />
          <MoneyRow label="Total" value={formatMoney(result.grandTotal, currency)} strong />
          {result.compValue !== null && (
            <MoneyRow label="Comp value" value={formatMoney(result.compValue, currency)} />
          )}
        </Stack>
      </Box>
    </Stack>
  );
}

function MoneyRow({
  label,
  value,
  strong = false,
}: {
  label: string;
  value: string;
  strong?: boolean;
}) {
  const theme = useTheme();
  return (
    <Stack direction="row" sx={{ justifyContent: 'space-between', gap: 1 }}>
      <Typography sx={{ fontSize: 13, color: inkAlpha(theme, 0.62) }}>{label}</Typography>
      <Typography sx={{ fontSize: 13, fontWeight: strong ? 600 : 400 }}>{value}</Typography>
    </Stack>
  );
}

function invitedLine(name: string, email: string, invited: boolean): string {
  const who = name.length > 0 ? name : email;
  if (invited) {
    return `We've invited ${who} (${email}) to claim their account and see this purchase.`;
  }
  return `${who} (${email}) already has access — this purchase is on their account.`;
}

/**
 * DNKAPI-0508's kept name — `matchedFirstName`/`matchedLastName` joined, or the typed email as a
 * last resort (mirrors `personLabel`'s fallback chain; both fields are only ever present
 * together, when `contactMatched` is true).
 */
function matchedContactName(result: WalkInSaleResult, email: string): string {
  const combined = `${result.matchedFirstName ?? ''} ${result.matchedLastName ?? ''}`.trim();
  return combined.length > 0 ? combined : email;
}

/**
 * DNKADM-0354 — the post-sale identity line. Reuses `invitedLine`'s existing copy pattern
 * unchanged (a copy-SOURCE fix, not a redesign); only the name fed into it changes. A
 * `contactMatched` sale names the person using the server-KEPT stored name
 * (`matchedFirstName`/`matchedLastName`) so staff can catch a wrong-person mistake by the
 * STORED identity, not their own (possibly mistyped) input. A genuinely new contact
 * (`contactMatched: false`) keeps using the typed name — unchanged.
 */
function contactSummaryLine(result: WalkInSaleResult, typedName: string, email: string): string {
  const who = result.contactMatched ? matchedContactName(result, email) : typedName;
  return invitedLine(who, email, result.invited);
}

// --- Catalog → rows --------------------------------------------------------------
//
// Duplicated from SellCartDialog rather than imported — see this file's header comment.

/** A server purchase cap, sanitized: `null`/absent stays uncapped, else floored to `>= 0`. */
function sanitizeCap(value: number | null | undefined): number | null {
  if (value == null) return null;
  if (!Number.isFinite(value)) return null;
  return Math.max(0, Math.floor(value));
}

/** Bound a quantity to `[0, cap]`; a negative is never reachable. */
function boundQuantity(next: number, cap: number | null): number {
  const floored = Math.max(0, Math.floor(Number.isFinite(next) ? next : 0));
  return cap === null ? floored : Math.min(floored, cap);
}

/** The quantity a row actually carries — clamped at the READ boundary (mirrors SellCartDialog). */
function quantityFor(quantities: Record<string, number>, row: BucketRow): number {
  return boundQuantity(quantities[row.key] ?? 0, row.cap);
}

/**
 * A sellable credit-bucket row, or nothing. Hidden: an UNPRICED bucket (this endpoint rejects
 * a line with no resolvable price) and one whose id is not a numeric `refId`. Returns an
 * array so it composes with `flatMap`.
 */
function toBucketRow(bucket: CreditBucket): BucketRow[] {
  const price = bucket.pricePerCredit;
  if (price == null || price <= 0) return [];
  const refId = Number(bucket.id);
  if (!Number.isFinite(refId)) return [];
  return [
    {
      key: bucket.id,
      refId,
      name: scrubBrand(bucket.name) ?? bucket.name,
      pricePerCredit: price,
      cap: sanitizeCap(bucket.maxPurchaseQuantity),
    },
  ];
}

/**
 * A sellable package row, or nothing. Hidden: an unpriced or free package (a comp order gives
 * away a free package, never a priced cart), a DRAFT or deactivated row, and one not offered at
 * the selected location (`locationIds` empty ⇒ everywhere; otherwise it must contain the
 * selected id). Mirrors `SellCartDialog`'s `toPackageRow`.
 */
function toPackageRow(offering: Offering, selectedLocationId: string | null): PackageRow[] {
  if (offering.price == null || offering.price <= 0) return [];
  if (offering.status === 'DRAFT' || !offering.active) return [];
  if (
    offering.locationIds.length > 0 &&
    (selectedLocationId === null || !offering.locationIds.includes(String(selectedLocationId)))
  ) {
    return [];
  }
  const refId = Number(offering.id);
  if (!Number.isFinite(refId)) return [];
  return [
    {
      key: offering.id,
      refId,
      name: scrubBrand(offering.name) ?? offering.name,
      price: offering.price,
      grants: offering.credits.map(
        (credit) => `${credit.amount} ${scrubBrand(credit.bucketName) ?? 'credits'}`,
      ),
    },
  ];
}

/** The client-side "before tax" estimate for the active sale mode — see this file's header. */
function estimateSubtotal(
  saleMode: SaleMode | null,
  bucketRows: BucketRow[],
  packageRows: PackageRow[],
  quantities: Record<string, number>,
  selectedPackageIds: string[],
  packagePriceEdits: Record<string, string>,
  selectedPlan: MembershipPlan | null,
  membershipRateEdit: string | undefined,
): number {
  if (saleMode === 'credit') {
    return bucketRows.reduce(
      (sum, row) => sum + row.pricePerCredit * quantityFor(quantities, row),
      0,
    );
  }
  if (saleMode === 'package') {
    return packageRows.reduce((sum, row) => {
      if (!selectedPackageIds.includes(row.key)) return sum;
      const edit = packagePriceEdits[row.key];
      if (edit === undefined) return sum + row.price;
      const n = Number(edit.trim());
      return sum + (Number.isFinite(n) && n > 0 ? n : row.price);
    }, 0);
  }
  if (saleMode === 'membership' && selectedPlan !== null) {
    if (membershipRateEdit === undefined) return selectedPlan.price;
    const n = Number(membershipRateEdit.trim());
    return Number.isFinite(n) && n > 0 ? n : selectedPlan.price;
  }
  return 0;
}

/** Whether `line` carries a set `overrideBase` — `credit` lines never have the field at all. */
function lineOverrideBase(line: WalkInLineInput): number | undefined {
  return 'overrideBase' in line ? line.overrideBase : undefined;
}

function externalPayment(
  locationId: number,
  idempotencyKey: string,
  status: ExternalStatus,
  paymentMethodId: string,
): ExternalPaymentInput {
  const payment: ExternalPaymentInput = { mode: 'external', locationId, idempotencyKey, status };
  if (status === 'paid' && paymentMethodId.trim().length > 0) {
    payment.paymentMethodId = paymentMethodId.trim();
  }
  return payment;
}

/** Map a settled-but-not-COMPLETED-and-not-parked order to what the front desk sees. */
function terminalSaleMessage(sale: WalkInSaleResult): string {
  const label = sale.orderNumber ?? sale.orderId ?? '';
  return unsettledOrderMessage(sale.status, label, {
    pending: 'Order {order} is still pending. Nothing has been granted yet.',
    failed: 'Payment declined. Order {order} failed. Nothing was charged or granted.',
  });
}

/** DNKADM-0472: a parked card sale still waiting after its action was driven and re-read. */
function parkedSaleMessage(sale: WalkInSaleResult): string {
  const label = sale.orderNumber ?? sale.orderId ?? '';
  return `Order ${label}: Waiting on the card. Check Payments before charging again.`;
}

/** The contact + order of a parked sale, or null when either is missing (nothing to read). */
function parkedOrderRef(sale: WalkInSaleResult): ParkedOrderRef | null {
  if (sale.contactId === null || sale.orderId === null) return null;
  return { contactId: sale.contactId, orderId: sale.orderId, sale };
}

/** The re-read order's state over the sale's own walk-in fields. */
function withOrderRead(sale: WalkInSaleResult, read: MembershipOrder): WalkInSaleResult {
  return { ...sale, ...read };
}

/**
 * Drive what a client CAN drive, in the SAME order as `SellCartDialog` (DNKADM-0245): a
 * top-level redirect first, then a nested `clientAction` (its own redirect first, else the
 * shared {@link getPaymentActionDriver} seam). Nothing here claims success; a driver that
 * refuses throws {@link UnsupportedPaymentActionError}, which the caller treats as "fall
 * through to the re-read".
 */
async function drivePaymentAction(action: MembershipOrder['paymentAction']): Promise<void> {
  if (action === null) return;
  const redirectUrl = asActionText(action['redirectUrl'] ?? action['url']);
  if (redirectUrl !== null) {
    window.location.assign(redirectUrl);
    return;
  }
  const clientAction = asActionRecord(action['clientAction']);
  if (clientAction === null) return;
  const nestedRedirectUrl = asActionText(clientAction['redirectUrl'] ?? clientAction['url']);
  if (nestedRedirectUrl !== null) {
    window.location.assign(nestedRedirectUrl);
    return;
  }
  await getPaymentActionDriver()(clientAction);
}

function asActionText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const text = value.trim();
  return text.length > 0 ? text : null;
}

/** A shallow copy of a plain-object client action, or null when it is not one. */
function asActionRecord(value: unknown): Record<string, unknown> | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  return { ...(value as Record<string, unknown>) };
}

function cartEmptyMessage(mode: SaleMode): string {
  if (mode === 'membership') return 'Choose a membership plan.';
  if (mode === 'package') return 'Add at least one package to the cart.';
  return 'Add at least one credit to the cart.';
}

function locationMessage(status: string): string {
  if (status === 'loading') return 'Loading locations before selling.';
  if (status === 'error') return 'Locations could not be loaded. Retry before selling.';
  return 'Select a location before selling.';
}

/**
 * Compose the walk-in error mapping with the SHARED negotiated-price error map
 * (`negotiatedPriceErrorView`), exactly as `SellCartDialog`/`BuyMembershipOrderDialog` do —
 * negotiated-price codes are checked FIRST since they need copy `toWalkInSaleErrorView` (an
 * api-layer module) does not carry.
 */
function toWalkInErrorView(error: unknown): SubmitError {
  const overrideView = negotiatedPriceErrorView(error);
  if (overrideView !== null) return overrideView;
  return toWalkInSaleErrorView(error);
}
